/* eslint no-underscore-dangle: ["error", {
	"allow": ['_loaded', '_loading', '_loadedRegular', '_loadedBold', '_loadedItalic', '_loadedBoldItalic', '_attempt', '_failed', '_addModel', '_updateModel']
}] */
import { FontModel } from '@sosocio/creator-types/app';
import * as DB from '@sosocio/creator-types/database';
import * as urlTools from '@sosocio/frontend-utils/url';
import axios, { AxiosRequestConfig } from 'axios';
import {
	ERRORS_INVALID_REQUEST_DATA,
	ERRORS_LOAD_FONT,
} from 'settings/errors';
import { ConfigModule } from 'store';
import getBucketUrl from 'tools/get-bucket-url';
import { isViteDevRuntime } from 'tools/runtime-env';
import _ from 'underscore';
import annotateError from 'utils/annotate-error';
import reportError from 'utils/report-error';
import sanitizeUrl from 'utils/sanitize-url';
import { reactive } from 'vue';
import {
	Action,
	Module,
	Mutation,
	VuexModule,
} from 'vuex-module-decorators';
import WebFont from 'webfontloader';

/** WebFont timeout (ms) for the first attempt at loading a font. */
const FIRST_ATTEMPT_WEBFONT_TIMEOUT = 10000;
/** WebFont timeout (ms) for each retry after the first attempt reported the font inactive. */
const RETRY_WEBFONT_TIMEOUT = 3000;
/** Number of WebFont attempts in one load cycle before the font is flagged as failed. */
const MAX_WEBFONT_ATTEMPTS = 3;
const configuredTextFontFaces = new WeakSet<FontFace>();
const disabledTextFontFeatures = '"calt" 0, "rand" 0';

/**
 * Applies stable text shaping defaults to every registered face that belongs
 * to the active WebFont family, while retaining any other feature descriptors.
 * The active family is intentionally kept intact so custom bold and italic
 * family aliases are configured independently.
 *
 * @param fontFamily The family name reported by WebFont for the active face.
 */
function configureTextFontFeatures(fontFamily: string): void {
	if (
		typeof document === 'undefined'
		|| !document.fonts
	) {
		return;
	}

	const normalizedFontFamily = normalizeFontFamilyName(fontFamily);

	document.fonts.forEach((fontFace) => {
		if (
			configuredTextFontFaces.has(fontFace)
			|| !('featureSettings' in fontFace)
			|| normalizeFontFamilyName(fontFace.family) !== normalizedFontFamily
		) {
			return;
		}

		const featureSettings = fontFace.featureSettings.trim();
		fontFace.featureSettings = (
			featureSettings
			&& featureSettings !== 'normal'
				? `${featureSettings}, ${disabledTextFontFeatures}`
				: disabledTextFontFeatures
		);
		configuredTextFontFaces.add(fontFace);
	});
}

/**
 * Configures a family immediately and retries after the document font set is
 * ready. Vite development treats Typekit fonts as preloaded, but their faces
 * can still register one microtask later than the store model.
 *
 * @param fontFamily The printable family expected in the document font set.
 * @returns A promise that resolves after the ready font set has been configured.
 */
function configureTextFontFeaturesWhenReady(fontFamily: string): Promise<void> {
	configureTextFontFeatures(fontFamily);

	if (
		typeof document === 'undefined'
		|| !document.fonts
	) {
		return Promise.resolve();
	}

	return document.fonts.ready.then(() => {
		configureTextFontFeatures(fontFamily);
	});
}

/**
 * Build the ERRORS_LOAD_FONT error with the failing font's identity attached as
 * Bugsnag metadata, so reports show *which* webfont failed instead of a bare
 * "Failed loading webfont". The context rides on the error via annotateError so
 * it survives the caught/unhandled paths this reject flows through.
 *
 * @param fontModel The font whose load failed.
 * @returns An Error tagged with the font's id, provider and (sanitized) url.
 */
function fontLoadError(fontModel: FontModel): Error {
	return annotateError(
		new Error(ERRORS_LOAD_FONT),
		'font',
		{
			id: fontModel.id,
			provider: fontModel.provider ?? null,
			url: fontModel.url ? sanitizeUrl(fontModel.url) : null,
		},
	);
}

const defaultsFontModel: Partial<FontModel> = {
	_attempt: 0,
	_attemptDatauri: 0,
	_datauri: '',
	_failed: false,
	_failedDatauri: false,
	_fontUrl: '',
	_loaded: false,
	_loadedBold: false,
	_loadedBoldItalic: false,
	_loadedItalic: false,
	_loadedRegular: false,
	_loading: false,
	_loadingDataUri: false,
};
const webFontLoaderClassRegex = /^wf-(active|inactive|loading|[a-z0-9-]+-(n|i)\d-(active|inactive|loading))$/u;
const urlTokenRegex = /url\(\s*(['"]?)([^'")]+)\1\s*\)/gi;

function removeWebFontLoaderClasses(): void {
	if (typeof document === 'undefined') {
		return;
	}

	for (const className of Array.from(document.documentElement.classList)) {
		if (webFontLoaderClassRegex.test(className)) {
			document.documentElement.classList.remove(className);
		}
	}
}

async function getInlineCSS(
	cssData: string,
	baseUrl: string,
): Promise<string> {
	/**
	 * Collect unique resolvable URLs
	 */
	const uniqueUrls = new Map<string, string>();

	for (const matches of cssData.matchAll(urlTokenRegex)) {
		const raw = matches[2].trim();

		if (!/^(data:|blob:|about:|#)/i.test(raw)) {
			try {
				const absoluteUrl = new URL(
					raw,
					baseUrl,
				).href;

				if (!uniqueUrls.has(raw)) {
					uniqueUrls.set(
						raw,
						absoluteUrl,
					);
				}
			} catch {
				/**
				 * Ignore malformed URLs
				 */
			}
		}
	}

	/**
	 * Fetch all in parallel (with caching by absolute URL)
	 */
	const absoluteUrls = new Map<string, Promise<string>>();
	const map = new Map<string, string>(
		await Promise.all(
			Array
				.from(uniqueUrls.entries())
				.map(async ([
					raw,
					absoluteUrl,
				]) => {
					if (!absoluteUrls.has(absoluteUrl)) {
						absoluteUrls.set(
							absoluteUrl,

							getURLContent(
								absoluteUrl,
								true,
							),
						);
					}

					return [
						raw,
						await absoluteUrls.get(absoluteUrl)!,
					] as const;
				}),
		),
	);

	/**
	 * Map raw URLs to data URIs and preserve the original quote characters
	 */
	return cssData.replace(
		urlTokenRegex,
		(
			full,
			quoteCharacter,
			raw,
		) => {
			const replacement = map.get(raw.trim());

			return (
				replacement
					? `url(${quoteCharacter}${replacement}${quoteCharacter})`
					: full
			);
		},
	);
}

function getURLContent(
	url: string,
	asUri = false,
): Promise<string> {
	const noCorsUrl = urlTools.appendParameter(
		url,
		'noCorsHeader',
	);
	let config: AxiosRequestConfig | undefined;

	if (asUri) {
		config = {
			responseType: 'blob',
		};
	}

	return axios
		.get(
			noCorsUrl,
			config,
		)
		.then((response) => response.data)
		.then(async (data: Blob | string) => {
			if (typeof data === 'string') {
				data = await getInlineCSS(
					data,
					url,
				);
			} else if (asUri) {
				await new Promise<void>((resolve, reject) => {
					try {
						const reader = new FileReader();
						reader.onloadend = () => {
							data = reader.result as string;
							resolve();
						};
						reader.readAsDataURL(data as Blob);
					} catch (error) {
						reject(error);
					}
				});
			}

			return data as string;
		})
		.catch((error) => {
			console.error(
				'Error while fetching font url content',
				error,
			);

			reportError(
				new Error('Error while fetching font url content'),
				'warning',
				(event) => {
					event.errors.push(error);
				},
			);

			throw error;
		});
}

/**
 * Normalizes browser-serialized font family names for comparison with the
 * family names emitted by WebFont Loader.
 *
 * @param fontFamily A raw FontFace or WebFont Loader family name.
 * @returns The trimmed family name without matching outer quotes.
 */
function normalizeFontFamilyName(fontFamily: string): string {
	return fontFamily
		.trim()
		.replace(
			/^(['"])(.*)\1$/,
			'$2',
		);
}

@Module({
	namespaced: true,
	name: 'font',
})
export default class Font extends VuexModule {
	public collection = reactive<FontModel[]>([]);

	public get getById(): (id: string) => FontModel | undefined {
		return (id: string) => this.collection.find((fontModel) => fontModel.id === id);
	}

	public get withNames(): (fontModels: FontModel[]) => FontModel[] {
		return (fontModels) => fontModels.map((model) => ({
			...model,
			name: (
				window.App?.router?.$i18next?.exists?.(`fonts:${model.id}`)
					? window.App.router.$t(`fonts:${model.id}`)
					: model.id
			),
		}));
	}

	public get getDefault() {
		return (
			this.collection.find((model) => model.isDefault)
			|| this.getPrintable[0]
		);
	}

	public get getModelUrl(): (id: string) => string {
		return (id) => {
			const fontModel = this.getById(id);

			if (!fontModel) {
				throw new Error('Could not find font model');
			}

			if (
				isViteDevRuntime()
				&& fontModel.provider === 'typekit'
			) {
				return '';
			}

			const fonts: WebFont.Config = {};

			if (fontModel.url) {
				const bucketUrl = getBucketUrl('webfonts');
				const path = `${bucketUrl}fonts/`;
				const fontUrl = fontModel.url.substring(
					0,
					4,
				) == 'http'
					? fontModel.url
					: path + fontModel.url;

				fonts.custom = {};
				fonts.custom.families = [];
				fonts.custom.urls = [];

				if (!fontModel._loadedRegular) {
					fonts.custom.families.push(`${fontModel.id}:n4`);
					fonts.custom.urls.push(fontUrl);
				}
				if (fontModel.bold && !fontModel._loadedBold) {
					fonts.custom.families.push(`${fontModel.id} Bold:n7`);
					fonts.custom.urls.push(fontUrl);
				}
				if (fontModel.italic && !fontModel._loadedItalic) {
					fonts.custom.families.push(`${fontModel.id} Italic:i4`);
					fonts.custom.urls.push(fontUrl);
				}
				if (fontModel.bolditalic && !fontModel._loadedBoldItalic) {
					fonts.custom.families.push(`${fontModel.id} BoldItalic:i7`);
					fonts.custom.urls.push(fontUrl);
				}
			} else if (fontModel.provider === 'typekit') {
				if (fontModel.kitID) {
					fonts.typekit = {
						id: fontModel.kitID,
					};
				} else {
					throw new Error('Missing font kitID');
				}
			} else if (fontModel.provider === 'google') {
				let families = `${fontModel.id}:`;

				if (!fontModel._loadedRegular) {
					families += '400,';
				}
				if (fontModel.bold && !fontModel._loadedBold) {
					families += '700,';
				}
				if (fontModel.italic && !fontModel._loadedItalic) {
					families += '400italic,';
				}
				if (fontModel.bolditalic && !fontModel._loadedBoldItalic) {
					families += '700italic,';
				}

				if (fontModel.subset && fontModel.subset != 'latin') {
					// Only at the subset to the font url if it is not just latin, otherwise it will fail
					families += `&subset=${fontModel.subset}`;
				}

				fonts.google = {
					families: [families],
				};
			}

			return WebFont.getFontApiUrl(fonts)[0];
		};
	}

	public get getPrintable() {
		return this.collection.filter((model) => model.print);
	}

	@Mutation
	private _addModel(data: FontModel) {
		this.collection.push(data);
	}

	@Mutation
	private _updateModel(data: Partial<FontModel>) {
		const fontModelIndex = this.collection.findIndex((fontModel) => fontModel.id === data.id);
		const model = this.collection[fontModelIndex];
		this.collection[fontModelIndex] = _.extend(
			model,
			data,
		);
	}

	@Action({
		rawError: true,
	})
	public addModel(data: OptionalExceptFor<FontModel, 'id'>): Promise<FontModel> {
		if (!data.id) {
			return Promise.reject(
				new Error(ERRORS_INVALID_REQUEST_DATA),
			);
		}

		data = _.extend(
			JSON.parse(JSON.stringify(defaultsFontModel)),
			data,
		);

		if (this.getById(data.id)) {
			this._updateModel(data);
		} else {
			this._addModel(data as FontModel);
		}

		const fontModel = this.getById(data.id);

		if (!fontModel) {
			return Promise.reject(
				new Error('Could not find font model'),
			);
		}

		if (
			fontModel.autoload
			&& !fontModel._loaded
		) {
			return this
				.loadModel(fontModel.id)
				.then(() => fontModel);
		}

		return Promise.resolve(fontModel);
	}

	@Action({
		rawError: true,
	})
	public addModels(arrData: OptionalExceptFor<DB.FontModel, 'id'>[]) {
		const arrPromises: Promise<FontModel>[] = [];
		arrData.forEach((data) => arrPromises.push(this.addModel(data)));

		return Promise.all(arrPromises);
	}

	/**
	 * Loads every configured face for a font model and tracks its readiness.
	 * Printable Typekit fonts supplied by Vite wait for the browser font set so
	 * their shaping features are configured before consumers can measure text.
	 *
	 * @param id Identifier of the {@link FontModel} to load.
	 * @returns The loaded {@link FontModel} after all required faces are ready.
	 */
	@Action({
		rawError: true,
	})
	public loadModel(id: string): Promise<FontModel> {
		const fontModel = this.getById(id);

		if (!fontModel) {
			return Promise.reject(
				new Error('Could not find font model'),
			);
		}

		removeWebFontLoaderClasses();

		if (fontModel._loaded) {
			return Promise.resolve(
				fontModel,
			);
		}

		if (fontModel._loading) {
			// Font is already loading, wait for loaded event
			return new Promise((resolve, reject) => {
				const int = window.setInterval(
					() => {
						if (fontModel._loaded) {
							window.clearInterval(int);
							resolve(fontModel);
						} else if (fontModel._failed) {
							window.clearInterval(int);
							reject(fontLoadError(fontModel));
						}
					},
					100,
				);
			});
		}

		/*
		 * A font that already exhausted a full load cycle gets a single attempt (the final one)
		 * instead of starting the 10 second + retries cycle again, so callers that load the same
		 * broken font again (e.g. each tap on "add text") are not held up for the whole cycle.
		 * That attempt keeps the full first-attempt timeout, so a font whose earlier failure came
		 * from a slow network or a suspended page can still load once conditions recover.
		 */
		const isReloadAfterFailure = fontModel._failed;

		this._updateModel({
			id: fontModel.id,
			_attempt: (
				isReloadAfterFailure
					? MAX_WEBFONT_ATTEMPTS
					: fontModel._attempt + 1
			),
			_failed: false,
			_loading: true,
		});

		if (
			isViteDevRuntime()
			&& fontModel.provider === 'typekit'
		) {
			if (fontModel.print === true) {
				return configureTextFontFeaturesWhenReady(fontModel.id)
					.then(() => {
						this._updateModel({
							id: fontModel.id,
							_loaded: true,
							_loadedBold: Boolean(fontModel.bold),
							_loadedBoldItalic: Boolean(fontModel.bolditalic),
							_loadedItalic: Boolean(fontModel.italic),
							_loadedRegular: true,
							_loading: false,
						});

						return fontModel;
					});
			}

			this._updateModel({
				id: fontModel.id,
				_loaded: true,
				_loadedBold: Boolean(fontModel.bold),
				_loadedBoldItalic: Boolean(fontModel.bolditalic),
				_loadedItalic: Boolean(fontModel.italic),
				_loadedRegular: true,
				_loading: false,
			});

			return Promise.resolve(fontModel);
		}

		return new Promise((resolve, reject) => {
			/*
			 * WebFont calls `fontinactive` once per failed variant, so the outcome of this attempt
			 * is only decided once WebFont reports the whole load as settled (`active` or
			 * `inactive`). Retrying from each `fontinactive` started one overlapping reload per
			 * failed variant. `isSettled` turns true once `fontactive` has seen every required
			 * variant, so a load that WebFont calls `active` without that (some variants failed,
			 * or a Typekit kit lacks a flagged variant) is handled by `onAttemptFailed` instead of
			 * leaving the font loading forever.
			 */
			let isSettled = false;
			const onAttemptFailed = (): void => {
				if (isSettled) {
					return;
				}

				isSettled = true;

				/*
				 * A Google or Typekit font whose regular face loaded is resolved rather than failed
				 * once retrying cannot help: after the final attempt, or straight away for Typekit,
				 * whose kit simply does not contain a variant the font model flags (a data problem
				 * that a reload does not fix). Those providers serve every style under one family
				 * name, so the partial `_loaded*` flags are kept and the browser synthesizes the
				 * missing styles from the regular face. Custom (url) fonts register each style as
				 * its own family ("X Bold"), which text drawing picks by name, so a missing face
				 * would silently render in a fallback font; they keep failing instead.
				 */
				if (
					fontModel._loadedRegular
					&& !fontModel.url
					&& (
						fontModel._attempt >= MAX_WEBFONT_ATTEMPTS
						|| fontModel.provider === 'typekit'
					)
				) {
					this._updateModel({
						id: fontModel.id,
						_failed: false,
						_loaded: true,
						_loading: false,
					});

					resolve(fontModel);
				} else if (fontModel._attempt >= MAX_WEBFONT_ATTEMPTS) {
					this._updateModel({
						id: fontModel.id,
						_loading: false,
						_failed: true,
					});

					reject(fontLoadError(fontModel));
				} else {
					this._updateModel({
						id: fontModel.id,
						_loading: false,
					});

					// Try again
					this
						.loadModel(fontModel.id)
						.then(
							resolve,
							reject,
						);
				}
			};
			/**
			 * Builds the Google Fonts family request for this font.
			 *
			 * @param includeLoaded Whether faces that already loaded are requested too.
			 * @returns The `family:variants` string WebFont passes to Google Fonts.
			 */
			const getGoogleFamilies = (includeLoaded: boolean): string => {
				let families = `${fontModel.id}:`;

				if (
					includeLoaded
					|| !fontModel._loadedRegular
				) {
					families += '400,';
				}
				if (
					fontModel.bold
					&& (
						includeLoaded
						|| !fontModel._loadedBold
					)
				) {
					families += '700,';
				}
				if (
					fontModel.italic
					&& (
						includeLoaded
						|| !fontModel._loadedItalic
					)
				) {
					families += '400italic,';
				}
				if (
					fontModel.bolditalic
					&& (
						includeLoaded
						|| !fontModel._loadedBoldItalic
					)
				) {
					families += '700italic,';
				}

				if (
					fontModel.subset
					&& fontModel.subset != 'latin'
				) {
					// Only at the subset to the font url if it is not just latin, otherwise it will fail
					families += `&subset=${fontModel.subset}`;
				}

				return families;
			};
			const fonts: WebFont.Config = {
				classes: false,
				/*
				 * WebFont's default timeout is 3 seconds, which makes slow networks (or iOS suspending
				 * the page while the file picker is open) report fonts as inactive even though they
				 * would load. The first attempt gets 10 seconds; the retries keep the 3 second default,
				 * so a font that genuinely fails is reported after about 16 seconds (10 + 3 + 3) rather
				 * than the 9 seconds the 3 second default gives three attempts. Once a font has failed,
				 * a later load makes a single 10 second attempt.
				 */
				timeout: (
					fontModel._attempt === 1 || isReloadAfterFailure
						? FIRST_ATTEMPT_WEBFONT_TIMEOUT
						: RETRY_WEBFONT_TIMEOUT
				),
				fontactive: (_fontFamily: string, fvd: string) => {
					if (fontModel.print === true) {
						configureTextFontFeatures(_fontFamily);
					}

					if (fontModel.url) {
						_fontFamily = _fontFamily.replace(
							' BoldItalic',
							'',
						);
						_fontFamily = _fontFamily.replace(
							' Bold',
							'',
						);
						_fontFamily = _fontFamily.replace(
							' Italic',
							'',
						);
					}

					if (fvd == 'n4') {
						// normal
						this._updateModel({
							id: fontModel.id,
							_loadedRegular: true,
						});
					} else if (fvd == 'i4') {
						// italic
						this._updateModel({
							id: fontModel.id,
							_loadedItalic: true,
						});
					} else if (fvd == 'n7') {
						// bold
						this._updateModel({
							id: fontModel.id,
							_loadedBold: true,
						});
					} else if (fvd == 'i7') {
						// bold italic
						this._updateModel({
							id: fontModel.id,
							_loadedBoldItalic: true,
						});
					}

					if (
						fontModel.bold
						&& !fontModel._loadedBold
					) {
						// Do nothing
					} else if (
						fontModel.italic
						&& !fontModel._loadedItalic
					) {
						// Do nothing
					} else if (
						fontModel.bolditalic
						&& !fontModel._loadedBoldItalic
					) {
						// Do nothing
					} else if (!fontModel._loadedRegular) {
						// Do nothing
					} else {
						isSettled = true;

						// Flag font as loaded after a short delay
						window.setTimeout(
							() => {
								this._updateModel({
									id: fontModel.id,
									_loaded: true,
									_loading: false,
								});

								resolve(fontModel);
							},
							ConfigModule['webfont.timeout'],
						);
					}
				},
				active: () => {
					// Some variants loaded, but not every variant this font needs
					if (!isSettled) {
						onAttemptFailed();
					}
				},
				inactive: onAttemptFailed,
			};

			if (fontModel.url) {
				const bucketUrl = getBucketUrl('webfonts');
				const path = `${bucketUrl}fonts/`;
				const fontUrl = fontModel.url.substring(
					0,
					4,
				) == 'http'
					? fontModel.url
					: path + fontModel.url;

				fonts.custom = {};
				fonts.custom.families = [];
				fonts.custom.urls = [];

				if (!fontModel._loadedRegular) {
					fonts.custom.families.push(`${fontModel.id}:n4`);
					fonts.custom.urls.push(fontUrl);
				}
				if (
					fontModel.bold
					&& !fontModel._loadedBold
				) {
					fonts.custom.families.push(`${fontModel.id} Bold:n7`);
					fonts.custom.urls.push(fontUrl);
				}
				if (
					fontModel.italic
					&& !fontModel._loadedItalic
				) {
					fonts.custom.families.push(`${fontModel.id} Italic:i4`);
					fonts.custom.urls.push(fontUrl);
				}
				if (
					fontModel.bolditalic
					&& !fontModel._loadedBoldItalic
				) {
					fonts.custom.families.push(`${fontModel.id} BoldItalic:i7`);
					fonts.custom.urls.push(fontUrl);
				}
			} else if (fontModel.provider === 'typekit') {
				if (fontModel.kitID) {
					fonts.typekit = {
						id: fontModel.kitID,
					};
				} else {
					// Flag the font as failed so later callers do not wait on it forever
					this._updateModel({
						id: fontModel.id,
						_failed: true,
						_loading: false,
					});
					reject(new Error('Missing font kitID'));

					return;
				}
			} else if (fontModel.provider === 'google') {
				fonts.google = {
					families: [getGoogleFamilies(false)],
				};
			}

			/*
			 * `_fontUrl` feeds `loadDataUri`, which embeds the CSS for SVG text, so it must cover
			 * every face of the font. A Google retry only requests the faces that failed, so the
			 * stored URL is built from all flagged faces instead of the retry request.
			 */
			const fontUrl = WebFont.getFontApiUrl(
				fonts.google
					? {
						...fonts,
						google: {
							families: [getGoogleFamilies(true)],
						},
					}
					: fonts,
			)[0];

			if (fontUrl) {
				this._updateModel({
					id: fontModel.id,
					_fontUrl: fontUrl,
				});
			}

			removeWebFontLoaderClasses();
			WebFont.load(fonts);
		});
	}

	@Action({
		rawError: true,
	})
	public loadDataUri(id: string): Promise<FontModel> {
		/* eslint-disable no-underscore-dangle */
		const fontModel = this.getById(id);

		if (!fontModel) {
			return Promise.reject(
				new Error('Could not find font model'),
			);
		}
		if (
			fontModel._datauri
			|| !fontModel._fontUrl
		) {
			return Promise.resolve(fontModel);
		}

		if (fontModel._loadingDataUri) {
			/**
			 * Font data uri is already loading, wait for loaded event
			 */
			return new Promise((resolve) => {
				const int = window.setInterval(
					() => {
						if (
							fontModel._datauri
							|| fontModel._failedDatauri
						) {
							window.clearInterval(int);
							resolve(fontModel);
						}
					},
					100,
				);
			});
		}

		this._updateModel({
			id: fontModel.id,
			_attemptDatauri: (
				fontModel._failedDatauri
					? 1
					: fontModel._attemptDatauri + 1
			),
			_failedDatauri: false,
			_loadingDataUri: true,
		});

		return getURLContent(fontModel._fontUrl)
			.then((response) => {
				this._updateModel({
					id: fontModel.id,
					_datauri: response,
					_loadingDataUri: false,
				});
			})
			.catch(() => {
				if (fontModel._attemptDatauri === 3) {
					return this._updateModel({
						id: fontModel.id,
						_loadingDataUri: false,
						_failedDatauri: true,
					});
				}

				this._updateModel({
					id: fontModel.id,
					_loadingDataUri: false,
				});

				return this
					.loadDataUri(fontModel.id)
					.then(() => undefined);
			})
			.then(() => fontModel);
		/* eslint-enable no-underscore-dangle */
	}
}
